import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const DOOR = ".ncl-accent-door, [data-deck-tab], [data-deck-inbox-rail]";
export function inactiveLightDoor(target: EventTarget | null): HTMLElement | null {
  const door = target instanceof Element ? target.closest<HTMLElement>(DOOR) : null;
  if (
    !door ||
    door.matches(
      '[aria-current="true"], [aria-current="page"], [aria-selected="true"], [aria-pressed="true"], [data-active-tab="true"], :disabled',
    )
  )
    return null;
  return door;
}
type Pool = {
  host: HTMLElement;
  left: number;
  top: number;
  width: number;
  height: number;
  accent: string;
};
/** Pointer and focus share one underlay seat; the actual button stays an opaque object above it. */
export function DoorHoverLight({ enabled }: { enabled: boolean }) {
  const [pool, setPool] = useState<Pool | null>(null);
  useEffect(() => {
    if (!enabled) {
      setPool(null);
      return;
    }
    let pointer: HTMLElement | null = null;
    let focused: HTMLElement | null = inactiveLightDoor(document.activeElement);
    let observed: HTMLElement | null = null;
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => paint());
    const paint = () => {
      const door = pointer ?? focused;
      if (door !== observed) {
        observer?.disconnect();
        if (door) observer?.observe(door);
        observed = door;
      }
      if (!door?.isConnected || !inactiveLightDoor(door)) {
        setPool(null);
        return;
      }
      const host =
        door.closest("[data-cleo-shell]")?.querySelector<HTMLElement>("[data-rail-card-host]") ??
        document.querySelector<HTMLElement>("[data-rail-card-host]");
      if (!host) {
        setPool(null);
        return;
      }
      const r = door.getBoundingClientRect(),
        h = host.getBoundingClientRect();
      const region = door.closest<HTMLElement>("[data-dock-room-rail-region], [data-context-dock]");
      if (region) {
        const b = region.getBoundingClientRect();
        if (r.top < b.top || r.bottom > b.bottom) {
          setPool(null);
          return;
        }
      }
      const width = Math.min(r.width, 220) + 110,
        height = r.height + 110;
      const style = getComputedStyle(door);
      setPool({
        host,
        left: r.left - h.left + r.width / 2 - width / 2,
        top: r.top - h.top + r.height / 2 - height / 2,
        width,
        height,
        accent:
          (door.hasAttribute("data-deck-inbox-rail")
            ? style.getPropertyValue("--rail-accent").trim()
            : "") ||
          style.getPropertyValue("--door-accent").trim() ||
          style.getPropertyValue("--tab-accent").trim() ||
          style.color,
      });
    };
    const over = (e: PointerEvent) => {
      pointer = inactiveLightDoor(e.target);
      paint();
    };
    const out = (e: PointerEvent) => {
      pointer = inactiveLightDoor(e.relatedTarget);
      paint();
    };
    const focus = (e: FocusEvent) => {
      focused = inactiveLightDoor(e.target);
      paint();
    };
    const blur = (e: FocusEvent) => {
      focused = inactiveLightDoor(e.relatedTarget);
      paint();
    };
    let clickFrame = 0;
    const afterClick = () => {
      cancelAnimationFrame(clickFrame);
      clickFrame = requestAnimationFrame(paint);
    };
    const currency = new MutationObserver(paint);
    currency.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-current", "aria-selected", "aria-pressed", "data-active-tab"],
    });
    document.addEventListener("click", afterClick);
    document.addEventListener("pointerover", over);
    document.addEventListener("pointerout", out);
    document.addEventListener("focusin", focus);
    document.addEventListener("focusout", blur);
    window.addEventListener("resize", paint);
    document.addEventListener("scroll", paint, true);
    document.documentElement.dataset.hoverLightReady = "true";
    paint();
    return () => {
      document.removeEventListener("pointerover", over);
      document.removeEventListener("pointerout", out);
      document.removeEventListener("focusin", focus);
      document.removeEventListener("focusout", blur);
      window.removeEventListener("resize", paint);
      document.removeEventListener("scroll", paint, true);
      document.removeEventListener("click", afterClick);
      cancelAnimationFrame(clickFrame);
      currency.disconnect();
      observer?.disconnect();
      delete document.documentElement.dataset.hoverLightReady;
    };
  }, [enabled]);
  return pool
    ? createPortal(
        <div
          aria-hidden="true"
          data-door-hover-light=""
          className="pointer-events-none absolute rounded-full forced-colors:hidden"
          style={{
            left: pool.left,
            top: pool.top,
            width: pool.width,
            height: pool.height,
            background: `radial-gradient(ellipse, color-mix(in oklab, ${pool.accent} max(var(--ncl-effect-hover-light-floor, 14%), calc(var(--ncl-effect-hover-light-alpha, 20%) * var(--ncl-effect-light-intensity, 1))), transparent) 0%, transparent 70%)`,
          }}
        />,
        pool.host,
      )
    : null;
}
